import { openDB, deleteDB, IDBPDatabase } from 'idb';

import { Logger } from '../logger';
import { IFileType } from '../file-type';
import { FileMeta } from '../../input/FileInputNext';

const dbName = 'file-cache';

const isIndexedDBAvailable = (): boolean =>
  typeof window !== 'undefined' &&
  'indexedDB' in window &&
  window.indexedDB != null;

export class FileCache {
  private db: IDBPDatabase | null = null;

  private initPromise: Promise<IDBPDatabase | null> | null = null;

  private memoryStore: Map<string, Blob> | null = null;

  constructor(
    private readonly logger: Logger,
    private readonly fileTypeChecker: IFileType,
  ) {}

  private async init() {
    if (this.db) {
      return this.db;
    }

    if (this.memoryStore) {
      return null;
    }

    if (!isIndexedDBAvailable()) {
      this.fallbackToMemory();
      return null;
    }

    if (!this.initPromise) {
      this.initPromise = this.openDatabase().finally(() => {
        this.initPromise = null;
      });
    }

    return this.initPromise;
  }

  private fallbackToMemory() {
    this.memoryStore = new Map();
    this.logger.warning(
      'IndexedDB недоступен, используется in-memory кэш (не переживает перезагрузку)',
      {
        tags: { vkdoc_error_type: 'idb' },
      },
    );
  }

  private async openDatabase() {
    try {
      this.db = await openDB('file-values', 1, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(dbName)) {
            db.createObjectStore(dbName);
          }
        },
      });

      this.db.onclose = () => {
        this.db = null;
      };
      this.db.onversionchange = () => {
        this.db?.close();
        this.db = null;
      };

      return this.db;
    } catch (error) {
      this.logger.error(error, {
        tags: {
          vkdoc_error_type: 'idb',
        },
      });
      this.fallbackToMemory();
      return null;
    }
  }

  private resizeImage(
    blob: Blob,
    maxWidth: number,
    maxHeight: number,
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = function () {
        URL.revokeObjectURL(url);

        let newWidth = img.width;
        let newHeight = img.height;

        if (img.width > maxWidth || img.height > maxHeight) {
          const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
          newWidth = Math.floor(img.width * ratio);
          newHeight = Math.floor(img.height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to create 2d context'));
          return;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        canvas.toBlob(
          (resizedBlob) => {
            if (resizedBlob) {
              resolve(resizedBlob);
            } else {
              reject(new Error('Failed to create resized blob'));
            }
          },
          'image/jpeg',
          0.8,
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const dataUrl = reader.result as string;
          const base64 = dataUrl.split(',')[1];

          if (!base64) {
            throw new Error('not a base64 url');
          }

          resolve(dataUrl);
        } catch (e) {
          reject(e);
        }
      };

      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  base64ToBlob(base64: string) {
    try {
      const parts = base64.split(',');
      const sliceSize = 512;

      const header = parts[0] || '';
      const base64Data = parts[1] || '';

      const mimeMatch = header.match(/:(.*?);/);

      if (!mimeMatch) {
        throw new Error('Could not extract MIME type from data URL');
      }

      const mimeType = mimeMatch[1];

      const byteCharacters = atob(base64Data);
      const byteArrays: BlobPart[] = [];

      for (
        let offset = 0;
        offset < byteCharacters.length;
        offset += sliceSize
      ) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        byteArrays.push(new Uint8Array(byteNumbers));
      }

      return new Blob(byteArrays as BlobPart[], { type: mimeType });
    } catch (error) {
      throw new Error(
        `Failed to convert base64 to blob: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  private async buildPreview(blob: Blob): Promise<Blob> {
    if (blob && blob.type.startsWith('image/')) {
      return this.resizeImage(blob, 300, 600);
    }
    return blob;
  }

  async save(blob: Blob): Promise<string | null> {
    try {
      const id = window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) =>
            (c === 'x'
              ? (Math.random() * 16) | 0
              : (Math.random() * 4) | 8
            ).toString(16),
          );

      const preview = await this.buildPreview(blob);

      const db = await this.init();

      if (this.memoryStore) {
        this.memoryStore.set(`${id}_full`, blob);
        this.memoryStore.set(`${id}_preview`, preview);
        return id;
      }

      if (!db) {
        return null;
      }

      const base64 = await this.blobToBase64(blob);
      let tx = db.transaction(dbName, 'readwrite');
      let store = tx.objectStore(dbName);
      await store.put(base64, `${id}_full`);
      await tx.done;

      const base64Preview = await this.blobToBase64(preview);
      tx = db.transaction(dbName, 'readwrite');
      store = tx.objectStore(dbName);
      await store.put(base64Preview, `${id}_preview`);
      await tx.done;

      return id;
    } catch (error) {
      this.logger.error(error, {
        tags: {
          vkdoc_error_type: 'idb',
        },
      });
      return null;
    }
  }

  async get(id: string) {
    try {
      const db = await this.init();

      if (this.memoryStore) {
        return this.memoryStore.get(`${id}_full`) ?? null;
      }

      if (!db) {
        return null;
      }

      const tx = db.transaction(dbName, 'readonly');
      const store = tx.objectStore(dbName);
      const retrievedBlob = await this.base64ToBlob(
        await store.get(`${id}_full`),
      );
      await tx.done;
      return (retrievedBlob as Blob) || null;
    } catch (error) {
      this.logger.error(error, {
        tags: {
          vkdoc_error_type: 'idb',
        },
      });
      return null;
    }
  }

  async getPreview(id: string) {
    try {
      const db = await this.init();

      if (this.memoryStore) {
        return this.memoryStore.get(`${id}_preview`) ?? null;
      }

      if (!db) {
        return null;
      }

      const tx = db.transaction(dbName, 'readonly');
      const store = tx.objectStore(dbName);
      const preview = await this.base64ToBlob(await store.get(`${id}_preview`));
      await tx.done;
      return (preview as Blob) || null;
    } catch (error) {
      this.logger.error(error, {
        tags: {
          vkdoc_error_type: 'idb',
        },
      });
      return null;
    }
  }

  async deleteDB() {
    try {
      this.memoryStore?.clear();

      if (!isIndexedDBAvailable()) {
        return;
      }

      await deleteDB('file-values');
    } catch (e) {}
  }

  private getImageResolution(
    file: Blob,
  ): Promise<{ width: number; height: number }> {
    const image: Promise<{ width: number; height: number }> = new Promise(
      (resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            resolve({ width: img.width, height: img.height });
          };
          img.onerror = reject;
          img.src = reader.result as string;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      },
    );

    return image;
  }

  async createFileMeta(blob: Blob, filename = ''): Promise<FileMeta> {
    const mimeType = await this.fileTypeChecker.fileTypeFromBlob(blob);

    if (mimeType.startsWith('image/')) {
      let width = 0;
      let height = 0;
      try {
        const resolution = await this.getImageResolution(blob);
        width = resolution.width;
        height = resolution.height;
      } catch (e) {}

      return {
        type: 'image',
        mimeType,
        size: blob.size,
        localCacheId: '',
        name: filename,
        width,
        height,
      };
    }

    return {
      type: 'general',
      mimeType,
      size: blob.size,
      localCacheId: '',
      name: filename,
    };
  }
}
